import { z } from 'zod';
/**
 * ehr#651 (Chase 10-25): the Provider Profile's supervisor settings (Forms and Supervision →
 * "I am a supervisor"). Two halves:
 *
 *   users."providerProfile".supervisor   the supervisor's own defaults (SupervisorProfile): whether
 *                                        they supervise, their supervisor license, and the cadence,
 *                                        type and hours a newly added provider starts with.
 *   provider_supervisions                one row per supervisor + provider they supervise, with that
 *                                        provider's own cadence, type, hours, start date and
 *                                        co-signature requirements. A provider may have several.
 *
 * The primary supervisor is not stored on the row. It is the provider's co-signing supervisor, the
 * one the note co-sign flow already uses (supervisionFrom: providerProfile.cosignerId while
 * cosignRequired is on, mirrored in users.supervisorId). Marking a row Primary sets that; the other
 * rows are Additional. So the progress-note reviewer and this list always agree.
 */
export const SUPERVISION_CADENCES = ['weekly', 'biweekly', 'monthly', 'as_needed'];
export const SUPERVISION_TYPES = ['individual', 'group', 'individual_group'];
/** The supervisor license choices; "Other / not listed" opens a text box (licenseOther). */
export const SUPERVISOR_LICENSES = [
    'LCSW · California',
    'LMFT · California',
    'LPCC · California',
    'Other / not listed',
];
/** At most a month of hours (31 days); decimals allowed (1.5, 4.5), kept to 2 places. */
export const MAX_SUPERVISION_HOURS = 744;
export const MAX_SUPERVISEES = 200;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const hours = z
    .number()
    .finite()
    .min(0, 'Hours cannot be below 0.')
    .max(MAX_SUPERVISION_HOURS, 'That is more hours than a month has.')
    .transform((v) => Math.round(v * 100) / 100);
export const supervisorProfileSchema = z.object({
    on: z.boolean().default(false),
    license: z.string().trim().max(80).default(''),
    licenseOther: z.string().trim().max(80).default(''),
    cadence: z.enum(SUPERVISION_CADENCES).default('weekly'),
    type: z.enum(SUPERVISION_TYPES).default('individual'),
    hours: hours.default(4),
});
export const SUPERVISOR_PROFILE_DEFAULTS = {
    on: false,
    license: '',
    licenseOther: '',
    cadence: 'weekly',
    type: 'individual',
    hours: 4,
};
/** Which documents need this supervisor's co-signature, per provider. */
export const supervisionCosignSchema = z.object({
    progressNotes: z.boolean().default(true),
    treatmentPlans: z.boolean().default(true),
    assessments: z.boolean().default(false),
    dischargeSummaries: z.boolean().default(false),
});
export const SUPERVISION_COSIGN_DEFAULTS = {
    progressNotes: true,
    treatmentPlans: true,
    assessments: false,
    dischargeSummaries: false,
};
/** One provider on a supervisor's "Providers I supervise" list, as the page sends and reads it. */
export const supervisionRowSchema = z.object({
    superviseeId: z.string().trim().min(1).max(64),
    primary: z.boolean().default(false),
    cadence: z.enum(SUPERVISION_CADENCES).default('weekly'),
    type: z.enum(SUPERVISION_TYPES).default('individual'),
    hours: hours.default(0),
    /** yyyy-mm-dd, or '' when not set. */
    start: z
        .string()
        .trim()
        .refine((v) => v === '' || ISO_DATE_RE.test(v), { message: 'Use a yyyy-mm-dd date.' })
        .default(''),
    cosign: supervisionCosignSchema.default(SUPERVISION_COSIGN_DEFAULTS),
});
export const supervisesSchema = z
    .array(supervisionRowSchema)
    .max(MAX_SUPERVISEES)
    .refine((rows) => new Set(rows.map((r) => r.superviseeId)).size === rows.length, {
    message: 'A provider is on the list twice.',
});
/**
 * ehr#651 follow-up (Chase 10-31): Provider Profile → Supervision → Supervisors, the provider's own
 * side of provider_supervisions: one entry per supervisor, with that row's settings. Saved by an admin
 * / manager (the Supervision section is theirs). Which one is Primary is not sent: it is the
 * provider's co-signing supervisor, picked in the same section.
 */
export const MAX_SUPERVISORS = 20;
export const supervisorLinkSchema = supervisionRowSchema
    .omit({ superviseeId: true, primary: true })
    .extend({ supervisorId: z.string().trim().min(1).max(64) });
export const supervisorsSchema = z
    .array(supervisorLinkSchema)
    .max(MAX_SUPERVISORS)
    .refine((rows) => new Set(rows.map((r) => r.supervisorId)).size === rows.length, {
    message: 'A supervisor is on the list twice.',
});
/** The supervisor license as it reads (a typed "Other" shows what was typed). */
export function supervisorLicenseLabel(p) {
    return p.license === 'Other / not listed' ? p.licenseOther || p.license : p.license;
}
/** A stored value → a clean SupervisorProfile (anything unreadable falls back to the defaults). */
export function normalizeSupervisorProfile(raw) {
    const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const full = supervisorProfileSchema.safeParse(o);
    if (full.success)
        return full.data;
    // Keep each field that reads on its own; the rest take their default.
    const out = { ...SUPERVISOR_PROFILE_DEFAULTS };
    const shape = supervisorProfileSchema.shape;
    for (const k of Object.keys(shape)) {
        const one = shape[k].safeParse(o[k]);
        if (one.success)
            out[k] = one.data;
    }
    return out;
}
//# sourceMappingURL=supervisionSettings.js.map